/**
 * The run's proposal lines as the designer's rows — one row per store × article, its sizes
 * inside it (brief part 2 §3 "Riassortimento" and §4 "Perché", `docs/adr/9032`, ADR 6006's
 * amendment of 2026-09-30).
 *
 * The proposal read publishes one line per store × article × size. The «Perché» list and the
 * «Approvazioni in attesa» queue both name a row «Negozio · Articolo» and show its sizes in the
 * detail, so the two pages share ONE row identity: `storeArticleRowId`. A link from «Perché» to the
 * queue with `?row=` therefore opens the same row (§4: «apre la stessa riga»).
 *
 * Every figure below is a field the platform published, or a count or a sum over one row's
 * lines in one unit (pairs). Nothing is estimated: a stock the ledger does not hold for every size
 * of the row is absent, never a partial sum presented as the whole.
 *
 * Pure, so the tests assert on arrays.
 */

import type { ProposalSummary, StockPositionSummary } from '@/api/generated';
import { sizeAxis, sizeName, type SizeName } from '@/planning';

import { isUndecided } from './dispositions';
import { isStoreTotalPosition } from './stock-summary';

/** The row's identity — the value of `?row=` on «Perché» and on the queue. */
export function storeArticleRowId(storeKey: string, articleKey: string): string {
  return `${storeKey}|${articleKey}`;
}

export interface StoreArticleRow {
  readonly id: string;
  readonly storeKey: string;
  readonly articleKey: string;
  /** Every line of the row, in the order the platform published them. */
  readonly lines: readonly ProposalSummary[];
  /** Σ units proposed — pairs. */
  readonly units: number;
  /** Σ units the doors requested — pairs. */
  readonly requested: number;
}

/** The run's lines grouped into rows, in the order each row's first line was published. */
export function storeArticleRows(proposals: readonly ProposalSummary[]): readonly StoreArticleRow[] {
  const byId = new Map<string, ProposalSummary[]>();
  for (const line of proposals) {
    const id = storeArticleRowId(line.store_key, line.article_key);
    const lines = byId.get(id);
    if (lines === undefined) byId.set(id, [line]);
    else lines.push(line);
  }
  return [...byId.entries()].map(([id, lines]) => ({
    id,
    storeKey: lines[0]!.store_key,
    articleKey: lines[0]!.article_key,
    lines,
    units: lines.reduce((sum, line) => sum + line.units, 0),
    requested: lines.reduce((sum, line) => sum + line.requested_units, 0),
  }));
}

/** The row's lines still waiting on a decision (ADR 6045): the only ones a decision may name. */
export function pendingLines(row: StoreArticleRow): readonly ProposalSummary[] {
  return row.lines.filter(isUndecided);
}

/**
 * The row a proposal line belongs to — how an old `?explain=<proposal_id>` link opens its row
 * now that the page speaks `?row=`.
 */
export function rowOfProposal(proposals: readonly ProposalSummary[], proposalId: string): string | null {
  const line = proposals.find((candidate) => candidate.proposal_id === proposalId);
  return line === undefined ? null : storeArticleRowId(line.store_key, line.article_key);
}

/** One driver, summed over the row's lines. Contributions are pairs, so they add. */
export interface RowDriver {
  readonly code: string;
  readonly contribution: number;
}

/** The row's drivers, summed by code across its sizes, largest absolute contribution first. */
export function rowDrivers(row: StoreArticleRow): readonly RowDriver[] {
  const byCode = new Map<string, number>();
  for (const line of row.lines) {
    for (const driver of line.drivers ?? []) {
      byCode.set(driver.code, (byCode.get(driver.code) ?? 0) + driver.contribution);
    }
  }
  return [...byCode.entries()]
    .map(([code, contribution]) => ({ code, contribution }))
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution) || a.code.localeCompare(b.code));
}

/**
 * The NEED decomposition of platform ADR 6038, in the order the waterfall reads it: what the
 * doors need, less what they hold, rounded to whole pairs — which adds up to the pairs requested.
 */
export const NEED_DRIVER_CODES = ['forecast_rate', 'cover_target', 'presentation_minimum', 'on_hand', 'in_transit', 'whole_units'] as const;
/**
 * The engine's change to that need — which takes the request to the proposal — and the pairs the
 * transport cost took off after the solve (`shipment_cost`, platform ADR 6188).
 */
export const ENGINE_DRIVER_CODES = ['allocation', 'case_rounding', 'dc_availability', 'shipment_cost'] as const;

export interface RowWaterfall {
  readonly need: readonly RowDriver[];
  readonly engine: readonly RowDriver[];
}

const BALANCE_TOLERANCE = 1e-6;

/**
 * The row's drivers as a waterfall: request first, then proposal. `null` unless it is one — every
 * code known, the need adding up to the pairs requested and the engine taking them to the pairs
 * proposed — so a bridge with a residual is never drawn (design/09 §8); the page falls back to the
 * diverging bars and its unbalanced notice. Measured on run e96dd7 (2026-10-06): all 8,558 lines
 * balance both ways. A code worth zero is left out.
 */
export function rowWaterfall(row: StoreArticleRow): RowWaterfall | null {
  const drivers = rowDrivers(row);
  const known = new Set<string>([...NEED_DRIVER_CODES, ...ENGINE_DRIVER_CODES]);
  if (drivers.length === 0 || drivers.some((driver) => !known.has(driver.code))) return null;
  const pick = (codes: readonly string[]): readonly RowDriver[] => codes
    .map((code) => drivers.find((driver) => driver.code === code))
    .filter((driver): driver is RowDriver => driver !== undefined && driver.contribution !== 0);
  const need = pick(NEED_DRIVER_CODES);
  const engine = pick(ENGINE_DRIVER_CODES);
  const sum = (part: readonly RowDriver[]): number => part.reduce((total, driver) => total + driver.contribution, 0);
  if (Math.abs(sum(need) - row.requested) > BALANCE_TOLERANCE) return null;
  if (Math.abs(row.requested + sum(engine) - row.units) > BALANCE_TOLERANCE) return null;
  return { need, engine };
}

/** One size of the row: what the ledger holds, what is proposed, and the two together. */
export interface RowSize {
  readonly name: SizeName;
  /** On hand at the door, from the stock ledger; `null` where the ledger has no row or no figure. */
  readonly onHand: number | null;
  /** Units proposed for the size; `null` where the row proposes nothing in it. */
  readonly proposed: number | null;
  /** On hand plus proposed; `null` unless both are known (a size with no proposal adds nothing). */
  readonly after: number | null;
  /** Weeks of cover the ledger reported; `null` where it did not. */
  readonly cover: number | null;
  /**
   * The row's store-total line, not a size (bata-platform ADR 6082, `docs/adr/6023`): its on hand
   * and cover are the article's at the store, every size summed by the platform and published on
   * the line, because the per-size ledger never holds that grain. Named «all sizes», never as a size.
   */
  readonly storeTotal: boolean;
  /**
   * The cell's pull target position as the run stored it (ADR 6057) — the larger of the
   * service-level lead-time demand and the size's share of the presentation minimum. `null` where
   * the run stored none, never nought: a target of nought is a door that should hold nothing.
   */
  readonly target: number | null;
  /**
   * Why `onHand` is what it is, for the cell's ⓘ: `reported`, `not_reported` (the feed sent the size
   * with no figure) or `null` — no ledger row for the size at all.
   */
  readonly positionState: string | null;
  /** The day the ledger's stock is as of; `null` without a ledger row. */
  readonly asOf: string | null;
  /**
   * Why the run wrote no line for the size (the platform's `uncovered_reason`, ADR 6057) — the
   * reason a size with nothing proposed has nought; `null` where the ledger states none.
   */
  readonly uncoveredReason: string | null;
  /**
   * The position the run netted the target with (on hand + in transit + on order, `committed_units`);
   * `null` without a ledger row or a figure. It tells «already has enough» from «demand too low» where
   * the reason is `need_below_one_unit` (platform ADR 6204).
   */
  readonly committed: number | null;
  /**
   * The size's «Formato estándar» position (platform ADR 2139, `size_slot`): the column 1..10 of
   * the size in its article's Oracle size group. From the size's proposal lines when they agree,
   * else from its ledger row (a ledger-only size has no line); `null` where neither knows one, and
   * always on a store-total entry, which is not a size.
   */
  readonly slot: number | null;
  /** The whole-pair need the run stored on the size's ledger row (`need_units`, ADR 6057); `null` without a row or a figure. */
  readonly need: number | null;
}

/**
 * The row's sizes in run order, joined to the ledger on store × article × size.
 *
 * `coverFromLines` lays each size's own proposal line's `weeks_of_cover` (bata-platform ADR 6073 B1)
 * where the ledger gives none — for a row whose ledger is NOT read (the queue reads one row's, not
 * fifty). It states the cover, never the stock: `onHand` stays `null` without a ledger row, and a
 * size whose lines disagree on their cover states none.
 */
export function rowSizes(
  row: StoreArticleRow, positions: readonly StockPositionSummary[], coverFromLines = false,
): readonly RowSize[] {
  const ledger = new Map<string, StockPositionSummary>();
  for (const position of positions) {
    if (position.store_key === row.storeKey && position.article_key === row.articleKey) {
      ledger.set(position.size_key, position);
    }
  }
  const proposedBySize = new Map<string, number>();
  for (const line of row.lines) proposedBySize.set(line.size_key, (proposedBySize.get(line.size_key) ?? 0) + line.units);
  const names = sizeAxis([...row.lines.map((line) => sizeName(line)), ...[...ledger.values()].map((position) => sizeName(position))]);
  // A store-total line's position is on the line itself (ADR 6082): the ledger has no row at ''.
  const totals = new Map<string, ProposalSummary>();
  for (const line of row.lines) if (isStoreTotalPosition(line)) totals.set(line.size_key, line);
  return names.map((name) => {
    const position = ledger.get(name.key);
    const total = position === undefined ? totals.get(name.key) : undefined;
    const onHand = position?.on_hand_units ?? total?.on_hand_units ?? null;
    const proposed = proposedBySize.get(name.key) ?? null;
    return {
      name,
      onHand,
      proposed,
      after: onHand === null ? null : onHand + (proposed ?? 0),
      cover: position !== undefined
        ? position.weeks_of_cover ?? null
        : total !== undefined
          ? total.weeks_of_cover ?? null
          : (coverFromLines ? shared(row.lines.filter((line) => line.size_key === name.key).map((line) => line.weeks_of_cover)) : null),
      target: position?.target_units ?? null,
      positionState: position?.position_state ?? null,
      asOf: position?.as_of ?? null,
      uncoveredReason: position?.uncovered_reason ?? null,
      committed: position?.committed_units ?? null,
      storeTotal: total !== undefined,
      need: position?.need_units ?? null,
      slot: total !== undefined
        ? null
        : shared(row.lines.filter((line) => line.size_key === name.key).map((line) => line.size_slot)) ?? position?.size_slot ?? null,
    };
  });
}

/**
 * The sizes ordered by their «Formato estándar» position (owner request 2026-10-27): slot
 * ascending, so a row whose sizes span two scales reads in the client's POS order (an Infantil 34
 * at slot 1 before the Adulto 35 at slot 2). A size with no slot keeps its relative order, after
 * the slotted ones; a store-total entry keeps the place it had. Stable and pure.
 */
export function sizesBySlot(sizes: readonly RowSize[]): readonly RowSize[] {
  const sized = sizes.filter((size) => !size.storeTotal);
  const ordered = sized
    .map((size, index) => ({ size, index }))
    .sort((a, b) => (a.size.slot ?? Number.POSITIVE_INFINITY) - (b.size.slot ?? Number.POSITIVE_INFINITY) || a.index - b.index)
    .map(({ size }) => size);
  let next = 0;
  return sizes.map((size) => (size.storeTotal ? size : ordered[next++]!));
}

/**
 * One size of the waterfall: the drivers, the pairs requested and proposed, and the constraint that
 * cut it — summed over the size's lines.
 */
export interface RowSizeSplit {
  /** Contribution by driver code, in pairs; a code the size's lines do not carry is absent. */
  readonly drivers: ReadonlyMap<string, number>;
  readonly requested: number;
  readonly proposed: number;
  /** The `bound_by` codes of the size's lines the engine cut (proposed below requested); empty where none was. */
  readonly cutBy: readonly string[];
}

export interface RowSizeSplits {
  /** The need driver codes non-zero on any size, in the waterfall's order. */
  readonly need: readonly string[];
  /** The engine driver codes non-zero on any size, in the waterfall's order. */
  readonly engine: readonly string[];
  /**
   * One entry per size of `sizes`; `null` where the row has no line in the size (a ledger-only
   * size) — it was asked for nothing, which is not the same as nought.
   */
  readonly sizes: readonly (RowSizeSplit | null)[];
}

/**
 * The row's waterfall split by size, on `rowSizes`' axis (run order, the ledger-only sizes
 * included). A code is kept when it is non-zero on ANY size, so each size's column still adds up
 * even where the code's row total is nought. Pure.
 */
export function rowSizeSplit(row: StoreArticleRow, sizes: readonly RowSize[]): RowSizeSplits {
  const present = new Set<string>();
  const split = sizes.map((size): RowSizeSplit | null => {
    const lines = row.lines.filter((line) => line.size_key === size.name.key);
    if (lines.length === 0) return null;
    const drivers = new Map<string, number>();
    for (const line of lines) {
      for (const driver of line.drivers ?? []) {
        drivers.set(driver.code, (drivers.get(driver.code) ?? 0) + driver.contribution);
        if (driver.contribution !== 0) present.add(driver.code);
      }
    }
    return {
      drivers,
      requested: lines.reduce((sum, line) => sum + line.requested_units, 0),
      proposed: lines.reduce((sum, line) => sum + line.units, 0),
      cutBy: [...new Set(lines.filter((line) => line.units < line.requested_units).map((line) => line.bound_by))],
    };
  });
  return {
    need: NEED_DRIVER_CODES.filter((code) => present.has(code)),
    engine: ENGINE_DRIVER_CODES.filter((code) => present.has(code)),
    sizes: split,
  };
}

/**
 * Σ of a per-size figure over the row — `null` unless EVERY size has it. A stock known for four
 * sizes of seven is not the row's stock.
 *
 * A store-total entry is not a size: it already IS the article's total at the store (ADR 6082), so
 * adding it to the sizes would count every pair twice. It stands for the row only where the row has
 * no size of its own to sum.
 */
export function wholeSum(sizes: readonly RowSize[], of: (size: RowSize) => number | null): number | null {
  const sized = sizes.filter((size) => !size.storeTotal);
  let sum = 0;
  for (const size of sized.length > 0 ? sized : sizes) {
    const value = of(size);
    if (value === null) return null;
    sum += value;
  }
  return sizes.length === 0 ? null : sum;
}

/** Σ over the sizes that have the figure, and how many of the row's sizes do. */
export interface ReportedSum {
  readonly sum: number;
  readonly reported: number;
  readonly sizes: number;
}

/**
 * Σ of a per-size figure over the sizes that have it (owner ruling 2026-10-14): a row's stock is
 * always a number, a size with no figure adding nothing, and `reported` of `sizes` says how much of
 * the row it covers. Same store-total rule as `wholeSum`.
 */
export function reportedSum(sizes: readonly RowSize[], of: (size: RowSize) => number | null): ReportedSum {
  const sized = sizes.filter((size) => !size.storeTotal);
  const counted = sized.length > 0 ? sized : sizes;
  const values = counted.map(of).filter((value): value is number => value !== null);
  return { sum: values.reduce((sum, value) => sum + value, 0), reported: values.length, sizes: counted.length };
}

/** The one value every line shares, or `null` where they differ or none has one. */
export function shared<T>(values: readonly (T | null | undefined)[]): T | null {
  const present = values.filter((value): value is T => value !== null && value !== undefined);
  if (present.length === 0) return null;
  return present.every((value) => value === present[0]) ? present[0]! : null;
}
